import React, { useState, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/layout/breadcrumb";
import {
  HelpCircle,
  Search,
  Mail,
  MessageSquare,
  BookOpen,
  FileQuestion,
  FileText,
  Settings,
  CreditCard,
  Users
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "wouter";

// Returns a relevance score for how well `text` matches `query`.
// Exact match > starts-with > all words present > substring.
function relevanceScore(text: string, query: string): number {
  const t = text.toLowerCase();
  const q = query.toLowerCase();
  if (t === q) return 10;
  if (t.startsWith(q)) return 6;
  const words = q.split(/\s+/).filter(Boolean);
  if (words.length > 1 && words.every(w => t.includes(w))) return 4;
  if (t.includes(q)) return 3;
  return 0;
}

// Extracts a ~160-char snippet of `text` centred around the first occurrence of `query`.
function getSnippet(text: string, query: string): string {
  const radius = 75;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text.length > 150 ? text.slice(0, 150) + "…" : text;
  const start = Math.max(0, idx - radius);
  const end = Math.min(text.length, idx + query.length + radius);
  return (start > 0 ? "…" : "") + text.slice(start, end) + (end < text.length ? "…" : "");
}

// Wraps every case-insensitive occurrence of `query` in a <mark> for highlighting.
function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-yellow-100 text-yellow-900 rounded px-0.5 not-italic font-medium">
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
}

// FAQ data
const faqData = [
  {
    category: "general",
    questions: [
      {
        id: "general-1",
        question: "What is SaaSGama?",
        answer: "SaaSGama is a comprehensive platform designed to help organizations track, manage, and optimize their software subscriptions and licenses. It provides features for monitoring application usage, license allocation, cost tracking, and generating optimization recommendations to reduce unnecessary spending."
      },
      {
        id: "general-2",
        question: "How do I get started with SaaSGama?",
        answer: "To get started, simply log in to your account, and you'll be guided through the initial setup process. You can begin by adding your applications and licenses, inviting team members, and exploring the dashboard to get familiar with the platform's features. Choose from Single ($29), Team ($49), or Enterprise ($199) plans."
      },
      {
        id: "general-3",
        question: "What are the main features of SaaSGama?",
        answer: "SaaSGama offers a wide range of features including software inventory management, license tracking, cost management, usage analytics, renewal alerts, optimization recommendations, user management, and customizable reporting. The platform includes comprehensive billing integration and supports CSV imports for bulk operations."
      }
    ]
  },
  {
    category: "licenses",
    questions: [
      {
        id: "licenses-1",
        question: "How do I add a new license?",
        answer: "To add a new license, navigate to the Licenses section, click on the 'Add License' button, and fill out the required information such as the application, license type, seats, cost, and renewal date. You can also associate the license with specific users or departments. CSV import is also supported for bulk license creation."
      },
      {
        id: "licenses-2",
        question: "Can I track when licenses are about to expire?",
        answer: "Yes, SaaSGama automatically tracks license expiration dates and provides renewal alerts. You can view upcoming renewals on the dashboard or set up email notifications through our Mailgun integration to be informed ahead of time."
      },
      {
        id: "licenses-3",
        question: "How do I assign licenses to users?",
        answer: "To assign licenses to users, go to the license details page, click on 'Manage Users', and select the users you want to assign the license to. You can also bulk assign licenses using the import functionality."
      }
    ]
  },
  {
    category: "billing",
    questions: [
      {
        id: "billing-1",
        question: "How does multi-currency support work?",
        answer: "SaaSGama supports multiple currencies including USD, EUR, GBP, INR, CAD, AUD, and JPY for tracking costs. You can set your preferred base currency in the settings, and all costs will be converted to this currency for reporting. You can also track licenses in their original purchase currency."
      },
      {
        id: "billing-2",
        question: "Can I track payments and invoices?",
        answer: "Yes, you can track payments and invoices by linking payment cards to licenses. This helps you monitor spending and generate detailed cost reports broken down by application, department, or time period."
      },
      {
        id: "billing-3",
        question: "How are renewal costs calculated?",
        answer: "Renewal costs are calculated based on the license's current cost and any applicable price increases. SaaSGama also provides historical cost tracking to help you identify trends and forecast future expenses."
      }
    ]
  }
];

// Support article categories
const articleCategories = [
  { id: "getting-started", title: "Getting Started", icon: <BookOpen className="h-5 w-5" />, count: 2 },
  { id: "account-settings", title: "Account Settings", icon: <Settings className="h-5 w-5" />, count: 2 },
  { id: "licensing", title: "Licensing & Permissions", icon: <FileText className="h-5 w-5" />, count: 1 },
  { id: "billing", title: "Billing & Payments", icon: <CreditCard className="h-5 w-5" />, count: 1 },
  { id: "users", title: "User Management", icon: <Users className="h-5 w-5" />, count: 1 },
  { id: "troubleshooting", title: "Troubleshooting", icon: <FileQuestion className="h-5 w-5" />, count: 3 },
];

// Popular articles data
const popularArticles = [
  { id: 1, title: "Getting started with SaaSGama", category: "Getting Started", views: 2345 },
  { id: 2, title: "How to import application data", category: "Data Management", views: 1987 },
  { id: 3, title: "Setting up license renewal alerts", category: "License Management", views: 1789 },
  { id: 4, title: "Understanding optimization recommendations", category: "Cost Optimization", views: 1654 },
  { id: 5, title: "Managing user permissions", category: "User Management", views: 1423 },
];

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("faq");
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const resultsRef = useRef<HTMLDivElement>(null);

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // FAQs: score by question first, then answer; exclude category-name-only matches.
  const matchingFaqs = normalizedSearch
    ? faqData
        .flatMap(category =>
          category.questions.map(faq => {
            const qScore = relevanceScore(faq.question, normalizedSearch);
            const aScore = relevanceScore(faq.answer, normalizedSearch);
            const score = Math.max(qScore, aScore * 0.8);
            return { ...faq, categoryLabel: category.category, score, snippet: getSnippet(faq.answer, normalizedSearch) };
          })
        )
        .filter(f => f.score > 0)
        .sort((a, b) => b.score - a.score)
    : [];

  // Articles: score by title (high weight) then category (lower weight).
  const matchingArticles = normalizedSearch
    ? popularArticles
        .map(article => ({
          ...article,
          score: Math.max(
            relevanceScore(article.title, normalizedSearch),
            relevanceScore(article.category, normalizedSearch) * 0.5
          ),
        }))
        .filter(a => a.score > 0)
        .sort((a, b) => b.score - a.score)
    : [];

  // Categories: normalise id hyphens to spaces so "getting started" matches "getting-started".
  const matchingCategories = normalizedSearch
    ? articleCategories
        .map(category => ({
          ...category,
          score: Math.max(
            relevanceScore(category.title, normalizedSearch),
            relevanceScore(category.id.replace(/-/g, " "), normalizedSearch) * 0.8
          ),
        }))
        .filter(c => c.score > 0)
        .sort((a, b) => b.score - a.score)
    : [];

  const hasSearchResults = matchingFaqs.length > 0 || matchingArticles.length > 0 || matchingCategories.length > 0;

  return (
    <div className="space-y-6">
      <Breadcrumb items={[
        { label: "Home", href: "/" },
        { label: "Help Center", href: "/help" }
      ]} />

      {/* Page header */}
      <div className="my-6">
        <h2 className="text-2xl font-bold text-slate-800">Help Center</h2>
        <p className="text-sm text-slate-500 mt-1">Find answers to common questions and learn how to use the platform</p>
      </div>

      {/* Search box */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="text-center max-w-2xl mx-auto mb-6">
            <HelpCircle className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">How can we help you today?</h3>
            <p className="text-sm text-muted-foreground mb-4">Search our knowledge base for answers or browse categories below</p>
          </div>
          
          <div className="relative max-w-xl mx-auto">
            <Input
              className="pl-10"
              placeholder="Search for articles, tutorials, or FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Button className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8" size="sm" onClick={handleSearch}>
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {normalizedSearch && (
        <div ref={resultsRef}>
        <Card>
          <CardHeader>
            <CardTitle>Search Results</CardTitle>
            <CardDescription>
              {hasSearchResults
                ? `Showing results for "${searchQuery.trim()}"`
                : `No results found for "${searchQuery.trim()}"`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {!hasSearchResults && (
              <div className="text-center py-8 text-muted-foreground">
                <FileQuestion className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No results found. Try different keywords or browse the categories below.</p>
              </div>
            )}

            {matchingFaqs.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                  FAQs <span className="font-normal text-muted-foreground">({matchingFaqs.length})</span>
                </h3>
                <Accordion type="single" collapsible className="w-full">
                  {matchingFaqs.map(faq => (
                    <AccordionItem key={faq.id} value={faq.id}>
                      <AccordionTrigger className="text-left text-sm">
                        <Highlight text={faq.question} query={normalizedSearch} />
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 capitalize">
                          {faq.categoryLabel}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <Highlight text={faq.snippet} query={normalizedSearch} />
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {matchingArticles.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                  Articles <span className="font-normal text-muted-foreground">({matchingArticles.length})</span>
                </h3>
                <div className="space-y-2">
                  {matchingArticles.map(article => (
                    <Link
                      key={article.id}
                      href={`/docs/article/${article.id}`}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-slate-50 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary shrink-0" />
                        <Highlight text={article.title} query={normalizedSearch} />
                      </span>
                      <span className="text-xs text-muted-foreground ml-4 shrink-0">{article.category}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {matchingCategories.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                  Categories <span className="font-normal text-muted-foreground">({matchingCategories.length})</span>
                </h3>
                <div className="grid gap-2 sm:grid-cols-2">
                  {matchingCategories.map(category => (
                    <Link
                      key={category.id}
                      href={`/docs/${category.id}`}
                      className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-slate-50 transition-colors"
                    >
                      <span className="text-primary">{category.icon}</span>
                      <Highlight text={category.title} query={normalizedSearch} />
                      <span className="ml-auto text-xs text-muted-foreground">{category.count} articles</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid w-full grid-cols-2 mb-6 h-auto">
          <TabsTrigger
            value="faq"
            className="min-w-0 flex-col gap-1 whitespace-normal py-2.5 text-center sm:flex-row sm:gap-0 sm:whitespace-nowrap"
          >
            <MessageSquare className="h-4 w-4 shrink-0 sm:mr-2" />
            <span className="text-xs leading-tight sm:text-sm">Frequently Asked Questions</span>
          </TabsTrigger>
          <TabsTrigger
            value="categories"
            className="min-w-0 flex-col gap-1 whitespace-normal py-2.5 text-center sm:flex-row sm:gap-0 sm:whitespace-nowrap"
          >
            <FileText className="h-4 w-4 shrink-0 sm:mr-2" />
            <span className="text-xs leading-tight sm:text-sm">Knowledge Base</span>
          </TabsTrigger>
        </TabsList>

        {/* FAQ Tab Content */}
        <TabsContent value="faq">
          <div className="grid gap-6 md:grid-cols-1">
            <Card>
              <CardHeader>
                <CardTitle>Frequently Asked Questions</CardTitle>
                <CardDescription>Find answers to common questions about using SaaSGama</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="general">
                  <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 mb-6 gap-1">
                    <TabsTrigger value="general">General</TabsTrigger>
                    <TabsTrigger value="licenses">Licenses</TabsTrigger>
                    <TabsTrigger value="billing">Billing</TabsTrigger>
                  </TabsList>
                  
                  {faqData.map(category => (
                    <TabsContent key={category.category} value={category.category}>
                      <Accordion type="single" collapsible className="w-full">
                        {category.questions.map(faq => (
                          <AccordionItem key={faq.id} value={faq.id}>
                            <AccordionTrigger className="text-left">
                              {faq.question}
                            </AccordionTrigger>
                            <AccordionContent>
                              <p className="text-muted-foreground">
                                {faq.answer}
                              </p>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Knowledge Base Tab Content */}
        <TabsContent value="categories">
          <div className="grid gap-6 md:grid-cols-3">
            {articleCategories.map(category => (
              <Card key={category.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-primary/10 text-primary">
                      {category.icon}
                    </div>
                    <CardTitle className="text-lg">{category.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    {category.count} articles in this category
                  </p>
                  <Button variant="outline" className="w-full" asChild>
                    <Link href={`/docs/${category.id}`}>
                      Browse Articles
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Popular Articles</CardTitle>
              <CardDescription>Most viewed help articles</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {popularArticles.map(article => (
                  <div key={article.id} className="flex items-center justify-between border-b pb-2 last:border-0">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <Link href={`/docs/article/${article.id}`} className="text-sm hover:text-primary transition-colors">
                        {article.title}
                      </Link>
                    </div>
                    <span className="text-xs text-muted-foreground">{article.views.toLocaleString()} views</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Contact support section */}
      <Card>
        <CardHeader>
          <CardTitle>Need more help?</CardTitle>
          <CardDescription>Contact our support team</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center">
            <div className="flex flex-col items-center text-center p-4 max-w-md">
              <div className="p-3 rounded-full bg-primary/10 text-primary mb-3">
                <Mail className="h-6 w-6" />
              </div>
              <h3 className="font-medium mb-2">Email Support</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Send us an email and we'll get back to you within 24 hours
              </p>
              <Button variant="outline" size="sm" asChild>
                <a href="mailto:support@saasgama.com">support@saasgama.com</a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
